// src/pages/curriculum/CurriculumAssignments.tsx
// Migrated to Supabase

import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { classMatchesLeaderArea } from '../../utils/curriculumAreaScope'
import {
  Save,
  Info,
  Filter,
  Download,
  Upload,
  Users,
  AlertTriangle,
  Book,
  UserIcon,
  Copy,
  Trash2,
  ChevronDown,
  ChevronUp,
  Search,
  X,
  CheckCircle,
  AlertCircle,
  Star,
  FileText,
  RefreshCw,
  Clock,
  Loader2,
  Settings,
  Eye,
  EyeOff,
  BarChart3,
  Hash,
} from 'lucide-react'
import PageHeader from '../../components/layout/PageHeader'
import Button from '../../components/ui/Button'
import Card from '../../components/ui/Card'
import { useToast } from '../../contexts/ToastContext'
import { useAuth } from '../../contexts/AuthContext'
import { useAcademicYear } from '../../contexts/AcademicYearContext'
import { supabase, DB_SCHEMA } from '../../supabase/config'
import { staffService } from '../../services/staffService'
import { gridsService } from '../../services/gridsService'
import { mapPreferenceLevel } from '../../utils/preferenceMatching'
import ReadOnlyGridViewer from '../../components/grid/ReadOnlyGridViewer'
import { displayLearningArea } from '../../constants/learningAreas'
import { YearSelector } from '../../components/common/YearSelector'

/**
 * The conflict target for a curriculum_preferences upsert.
 *
 * Postgres requires ON CONFLICT to name a unique index EXACTLY, and the only
 * unique index on this table is (tenant_id, grid_id, subject_id) —
 * `curriculum_preferences_tenant_6bd61686785db843a1b1a140be79f3`. Naming just
 * (grid_id, subject_id) is not "close enough": it fails every save with
 *   42P10 there is no unique or exclusion constraint matching the ON CONFLICT specification
 * which is what a curriculum leader hit on the first real attempt (issue #180).
 *
 * `tenant_id` is not sent in the payload — the column defaults to
 * `pit.current_tenant()`, which resolves from the signed-in user. It still
 * belongs in the conflict target, because conflict detection runs on the row
 * after defaults are applied.
 *
 * This is the same trap as #154 and #155: naming an index that does not exist.
 * Check pg_indexes before writing an onConflict, never infer it from the columns.
 */
const CURRICULUM_PREFERENCES_CONFLICT_TARGET = 'tenant_id,grid_id,subject_id'

// Types for curriculum leader preferences
interface TeacherPreference {
  teacherId: string
  teacherCode: string
  teacherName: string
  preferenceLevel: 'perfect_fit' | 'great_fit' | 'good_fit' | 'avoid'
  notes?: string
  addedBy?: string
  addedAt?: Date
}

interface SubjectPreferences {
  subjectId: string
  subjectName: string
  curriculumArea: string
  level?: string
  teacherPreferences: TeacherPreference[]
  lastModified?: Date
  modifiedBy?: string
}

// New interface for grouped subjects
interface GroupedSubject extends SubjectPreferences {
  classCount: number  // Number of times this subject runs
  classIds: string[]  // All the individual class IDs
  gridNames: string[] // Names of grids where this subject appears
}

interface TeacherSubjectPreference {
  subjectId: string
  subjectName: string
  curriculumArea: string
  preferenceLevel: 'perfect_fit' | 'great_fit' | 'good_fit' | 'avoid'
  notes?: string
}

interface TeacherWithPreferences {
  teacherId: string
  teacherCode: string
  teacherName: string
  department: string
  subjectPreferences: TeacherSubjectPreference[]
}

interface BulkAssignModalState {
  mode: 'subjects' | 'teachers'
  selectedItems: string[]
  searchTerm: string
  filteredItems: any[]
  targetSelection: string[]
  preferenceLevel: 'perfect_fit' | 'great_fit' | 'good_fit' | 'avoid'
}

interface PreferenceFilters {
  onlyUnassigned: boolean
  onlyConflicts: boolean
  preferenceLevel: 'all' | 'perfect_fit' | 'great_fit' | 'good_fit' | 'avoid'
}

// Helper function to format preference levels for display
const formatPreferenceLevel = (level: string): string => {
  switch (level) {
    case 'perfect_fit': return 'Perfect Fit'
    case 'great_fit': return 'Great Fit'
    case 'good_fit': return 'Good Fit'
    case 'avoid': return 'Avoid'
    default: return level.charAt(0).toUpperCase() + level.slice(1)
  }
}

const CurriculumAssignments: React.FC = () => {
  const { addToast } = useToast()
  const navigate = useNavigate()
  const { user } = useAuth()
  // Nominations are made against a specific year's grids. Everything on this page
  // — the subject list, the saved nominations, and where a save lands — follows
  // the year selector.
  const { workingYear } = useAcademicYear()
  
  // Local state for staff and grids data
  const [staff, setStaff] = useState<any[]>([])
  const [allGrids, setAllGrids] = useState<any[]>([])

  // Check if user has access to this page
  const hasAccess = user?.role === 'admin' || user?.role === 'curriculum_leader' || user?.role === 'assistant_principal' || user?.role === 'head_of_department'
  
  // Early return if no access
  if (!hasAccess) {
    return (
      <div>
        <PageHeader
          title="Access Denied"
          description="You don't have permission to access learning area assignments"
        />
        <Card>
          <div className="p-12 text-center">
            <AlertTriangle className="h-12 w-12 text-danger-fg mx-auto" />
            <h3 className="mt-2 text-sm font-medium text-ink-900">Insufficient Permissions</h3>
            <p className="mt-1 text-sm text-ink-500">
              This page is restricted to Curriculum Leaders and Administrators only.
            </p>
            <div className="mt-6">
              <Button variant="primary" onClick={() => navigate('/dashboard')}>
                Return to Dashboard
              </Button>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  // Core state - Updated to use grouped subjects
  const [groupedSubjects, setGroupedSubjects] = useState<GroupedSubject[]>([])
  const [filteredGroupedSubjects, setFilteredGroupedSubjects] = useState<GroupedSubject[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [levelFilter, setLevelFilter] = useState<string>('all')
  const [curriculumAreaFilter, setCurriculumAreaFilter] = useState<string>('all')
  const [selectedSubject, setSelectedSubject] = useState<GroupedSubject | null>(null)
  const [showGridView, setShowGridView] = useState(false)
  const [selectedGridForView, setSelectedGridForView] = useState<any | null>(null)
  const [showPreferenceModal, setShowPreferenceModal] = useState(false)
  
  // Enhanced state
  const [viewMode, setViewMode] = useState<'subjects' | 'teachers'>('subjects')
  const [selectedTeacher, setSelectedTeacher] = useState<TeacherWithPreferences | null>(null)
  const [showTeacherPreferenceModal, setShowTeacherPreferenceModal] = useState(false)
  const [filteredTeachers, setFilteredTeachers] = useState<TeacherWithPreferences[]>([])
  const [teachersWithPreferences, setTeachersWithPreferences] = useState<TeacherWithPreferences[]>([])
  const [departmentFilter, setDepartmentFilter] = useState<string>('all')
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set())
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null)
  const [showCompactView, setShowCompactView] = useState(false)
  const [advancedFilters, setAdvancedFilters] = useState<PreferenceFilters>({
    onlyUnassigned: false,
    onlyConflicts: false,
    preferenceLevel: 'all',
  })
  const [bulkAssignState, setBulkAssignState] = useState<BulkAssignModalState>({
    mode: 'subjects',
    selectedItems: [],
    searchTerm: '',
    filteredItems: [],
    targetSelection: [],
    preferenceLevel: 'perfect_fit'
  })
  const [subjects, setSubjects] = useState<Array<{id: string, name: string, curriculumArea?: string}>>([])
  const [realtimeSubscription, setRealtimeSubscription] = useState<any>(null)

  // Check if user is a curriculum leader
  const isCurriculumLeader = user?.role === 'curriculum_leader'
  const isAssistantPrincipal = user?.role === 'assistant_principal'
  const isHeadOfDepartment = user?.role === 'head_of_department'
  const userCurriculumArea = user?.curriculumArea || ''

  // Additional debug for access issues
  if (user && !hasAccess) {
    console.error('🚫 ACCESS DENIED - User has insufficient permissions:', {
      email: user.email,
      role: user.role,
      expectedRole: 'curriculum_leader or admin',
      hasAccess
    })
  }
  
  if (isCurriculumLeader && !userCurriculumArea) {
    console.error('🚫 CURRICULUM AREA MISSING - Curriculum leader without curriculum area:', {
      email: user?.email,
      name: user?.name,
      role: user?.role,
      curriculumArea: user?.curriculumArea
    })
  }

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load staff data
        const staffData = await staffService.getAllStaff()
        setStaff(staffData || [])
        
        // Load the grids for the year being planned ONLY. getAllGrids() returns
        // every year at once, which merged 2026's subjects and nominations into a
        // list a leader believed was next year's (issue #172).
        const gridsData = await gridsService.getGridsByYear(workingYear)
        setAllGrids(gridsData || [])
        
        // Subjects and their curriculum areas are derived from the school's own
        // grids (below) and the class-name heuristic in getClassCurriculumArea —
        // not from bundled sample data.
        setSubjects([]);
      } catch (error) {
        console.error('Error loading initial data:', error)
        setSubjects([])
      }
    }
    
    if (user) {
      loadInitialData()
    }
  }, [user, workingYear])

  // Helper function to extract level from subject name
  const extractLevel = (subjectName: string): string | undefined => {
    const subject = subjectName.toUpperCase()
    
    // Check for year levels in class names like 'ENGLISH12'
    if (subject.match(/7|8|9|10/)) return 'junior'
    if (subject.match(/11|12/) || subject.includes('VCE')) return 'senior'
    if (subject.includes('ORATORY')) return 'oratory'
    
    return undefined
  }

  // Helper function to display level names nicely
  const formatLevelName = (level?: string): string => {
    if (!level) return '-'
    
    const levelNames: Record<string, string> = {
      'oratory': 'Oratory',
      'junior': 'Junior (7-10)',
      'senior': 'Senior (11-12)'
    }
    
    return levelNames[level] || level.charAt(0).toUpperCase() + level.slice(1)
  }

  /**
   * The teacher's real staff code, or null.
   *
   * This MUST be the same value the Timetable Editor matches on. DataContext sets
   * `initial` from `staff_code` (src/contexts/DataContext.tsx), and
   * preferenceMatching compares nominations against that. staffService, which
   * feeds this screen, returns the raw row — where `initial` is NULL for all 229
   * staff and the code lives in snake_case `staff_code`.
   *
   * The old fallback guessed initials from the name ("Adrian Schober" -> "AS").
   * Only 19 of 220 active staff have a code equal to their name initials, so ~91%
   * of nominations were saved against a code that matches no one and scored zero
   * in the editor, silently (issue #172). A guessed code is worse than none: it
   * looks saved and does nothing. Returning null lets callers skip the teacher.
   */
  const getStaffCode = (teacher: any): string | null =>
    teacher?.staff_code || teacher?.staffCode || teacher?.initial || null

  // Load preferences from Supabase with real-time sync and grouping
  useEffect(() => {
    if (!allGrids || allGrids.length === 0) {
      // Don't return early - allow the UI to handle this state through the render logic
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    
    const loadPreferences = async () => {
      try {
        // Nominations are per (grid, subject), and grids are per year — so read
        // only the rows belonging to the year being planned. Without this a 2027
        // leader was shown 2026's saved nominations (issue #172).
        const gridIds = (allGrids || []).map(g => g.id).filter(Boolean)
        const { data: prefsData, error } = gridIds.length
          ? await supabase
              .from('curriculum_preferences')
              .select('*')
              .in('grid_id', gridIds)
          : { data: [], error: null }

        if (error) {
          console.error('❌ Error loading curriculum preferences:', error);
        }

        const loadedPrefs = new Map<string, any>();

        // Load from Supabase if available
        prefsData?.forEach((pref) => {
          // Extract data from the correct fields based on the actual database structure
          const subjectName = pref.subject_name || pref.subject_id || pref.preferences?.subjectName
          const teacherPreferences = pref.teacher_preferences || pref.preferences?.teacherPreferences || []

          if (subjectName) {
            loadedPrefs.set(subjectName, {
              // Saved rows carry 'high' / 'medium' / 'low'; this screen's badges
              // expect 'perfect_fit' / 'great_fit' / 'good_fit'. Unmapped values
              // fell through to the danger style, so a predecessor's top pick
              // rendered as a red "Avoid" chip (issue #172).
              teacherPreferences: (teacherPreferences || []).map((tp: any) => ({
                ...tp,
                preferenceLevel: mapPreferenceLevel(String(tp?.preferenceLevel || 'good_fit')),
              })),
              lastModified: pref.updated_at ? new Date(pref.updated_at) : null,
              modifiedBy: pref.preferences?.modifiedBy
            })
          }
        });

        // Teacher preferences come from the school's own saved data. When there
        // are none yet, show none rather than seeding sample data.

        // Group subjects by className across all grids
        const subjectGroups = new Map<string, {
          classes: any[],
          curriculumArea: string,
          level?: string,
          gridNames: string[]
        }>()

        allGrids.forEach((grid, gridIndex) => {
          // Skip this grid if no classes found
          if (!grid.classes || !Array.isArray(grid.classes) || grid.classes.length === 0) {
            return;
          }

          grid.classes
            .filter((c: any) => {
              // Assistant principals and heads of department see every learning area.
              if (isAssistantPrincipal || isHeadOfDepartment) {
                return true;
              }

              // A curriculum leader sees only their own learning area. Both sides are
              // folded onto a canonical name first, because the leader’s staff record
              // and the grid class use different vocabularies for the same area
              // ("Health & PE" vs "HPE"); compared raw, the HPE leader matched nothing
              // and the screen came up empty (issue #187).
              if (isCurriculumLeader && userCurriculumArea) {
                return classMatchesLeaderArea(c, userCurriculumArea, grid.name);
              }

              return true; // Everyone else sees all subjects
            })
            .forEach((c: any) => {
              const key = c.className // Use className as the grouping key
              
              if (!subjectGroups.has(key)) {
                const level = extractLevel(c.className)
                
                subjectGroups.set(key, {
                  classes: [],
                  curriculumArea: c.curriculumArea || userCurriculumArea || 'General',
                  level,
                  gridNames: []
                })
              }
              
              const group = subjectGroups.get(key)!
              group.classes.push(c)
              
              // Update curriculum area if this class has better info than the current group
              if (c.curriculumArea && (group.curriculumArea === 'General' || group.curriculumArea === 'Unknown')) {
                group.curriculumArea = c.curriculumArea
              }
              
              if (!group.gridNames.includes(grid.name)) {
                group.gridNames.push(grid.name)
              }
            })
        })

        // Create grouped subjects
        const grouped: GroupedSubject[] = Array.from(subjectGroups.entries()).map(([subjectName, group]) => {
          const savedData = loadedPrefs.get(subjectName)
          
          return {
            subjectId: subjectName,
            subjectName: subjectName,
            level: group.level,
            curriculumArea: group.curriculumArea,
            classCount: group.classes.length,
            classIds: group.classes.map(c => c.id || c.className),
            gridNames: group.gridNames,
            teacherPreferences: savedData?.teacherPreferences || [],
            lastModified: savedData?.lastModified,
            modifiedBy: savedData?.modifiedBy
          }
        })

        // Sort by subject name
        grouped.sort((a, b) => a.subjectName.localeCompare(b.subjectName))

        setGroupedSubjects(grouped)
        setFilteredGroupedSubjects(grouped)
        generateTeacherViewData(grouped)
        setLastSyncTime(new Date())
        setIsLoading(false)
      } catch (error) {
        console.error('Error processing assignments:', error)
        addToast('Error loading assignments', 'error')
        setIsLoading(false)
      }
    }
    
    loadPreferences()
    
    // Set up real-time subscription for curriculum_preferences changes
    const subscription = supabase
      .channel('curriculum_assignments')
      .on('postgres_changes',
        { event: '*', schema: DB_SCHEMA, table: 'curriculum_preferences' },
        () => {
          loadPreferences()
        }
      )
      .subscribe()
    
    setRealtimeSubscription(subscription)
    
    return () => {
      subscription.unsubscribe()
    }
  }, [allGrids, isCurriculumLeader, userCurriculumArea, subjects, workingYear])

  // Transform subject-centric data to teacher-centric data
  const generateTeacherViewData = useCallback((subjects: GroupedSubject[]) => {
    const teacherMap = new Map<string, TeacherWithPreferences>()
    
    if (staff && staff.length > 0) {
      staff.forEach((teacher) => {
        const isActive = teacher.status === 'active' || 
                        teacher.active === true || 
                        teacher.isActive === true ||
                        (!teacher.status && !teacher.hasOwnProperty('active') && !teacher.hasOwnProperty('isActive'))
        
        if (isActive) {
          const initial = getStaffCode(teacher) || ''
          
          teacherMap.set(teacher.id, {
            teacherId: teacher.id,
            teacherCode: initial,
            teacherName: teacher.name || teacher.fullName || 'Unknown Teacher',
            department: teacher.department || 'No Department',
            subjectPreferences: [],
          })
        }
      })
    }

    // Add preferences to teachers
    subjects.forEach((subject) => {
      subject.teacherPreferences.forEach((pref) => {
        const teacher = teacherMap.get(pref.teacherId)
        if (teacher) {
          teacher.subjectPreferences.push({
            subjectId: subject.subjectId,
            subjectName: subject.subjectName,
            curriculumArea: subject.curriculumArea,
            preferenceLevel: pref.preferenceLevel,
            notes: pref.notes,
          })
        }
      })
    })

    const teachersArray = Array.from(teacherMap.values())
    setTeachersWithPreferences(teachersArray)
    setFilteredTeachers(teachersArray)
  }, [staff])

  // Update teacher view data when subject preferences change
  useEffect(() => {
    generateTeacherViewData(groupedSubjects)
  }, [groupedSubjects, generateTeacherViewData])

  // Apply filters to grouped subjects
  useEffect(() => {
    if (!groupedSubjects.length) return

    let filtered = groupedSubjects

    // Apply search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (s) =>
          s.subjectName.toLowerCase().includes(lowerSearch) ||
          s.curriculumArea.toLowerCase().includes(lowerSearch)
      )
    }

    // Apply curriculum area filter
    if (curriculumAreaFilter !== 'all') {
      filtered = filtered.filter((s) => s.curriculumArea === curriculumAreaFilter)
    }

    // Apply level filter
    if (levelFilter !== 'all') {
      filtered = filtered.filter((s) => s.level === levelFilter)
    }

    // Apply advanced filters
    if (advancedFilters.onlyUnassigned) {
      filtered = filtered.filter(s => s.teacherPreferences.length === 0)
    }

    if (advancedFilters.preferenceLevel !== 'all') {
      filtered = filtered.filter(s =>
        s.teacherPreferences.some(p => p.preferenceLevel === advancedFilters.preferenceLevel)
      )
    }

    setFilteredGroupedSubjects(filtered)
  }, [searchTerm, levelFilter, curriculumAreaFilter, groupedSubjects, advancedFilters])

  // Filter teachers
  useEffect(() => {
    if (!teachersWithPreferences.length) return

    let filtered = teachersWithPreferences

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (t) =>
          t.teacherName.toLowerCase().includes(lowerSearch) ||
          t.teacherCode.toLowerCase().includes(lowerSearch) ||
          t.department.toLowerCase().includes(lowerSearch)
      )
    }

    if (departmentFilter !== 'all') {
      filtered = filtered.filter((t) => t.department === departmentFilter)
    }

    setFilteredTeachers(filtered)
  }, [searchTerm, departmentFilter, teachersWithPreferences])

  /**
   * Build the `curriculum_preferences` rows for one subject.
   *
   * The table's grain is one row per **(grid, subject)** — columns `grid_id`,
   * `subject_id`, `subject_name`, `teacher_preferences`, with a unique index on
   * (tenant_id, grid_id, subject_id). This screen groups a subject across grids,
   * so one subject fans out to one row per grid that actually runs it.
   *
   * The previous payload wrote `{ staff_id, preferences, year }` — three columns
   * that do not exist on the table — with `onConflict: 'staff_id,year'`, an index
   * that does not exist either. Every save failed with a red toast and nothing was
   * ever persisted (issue #172, and the cause of #154).
   *
   * `tenant_id` is stamped by installTenantStamping in src/supabase/config.ts.
   */
  const buildPreferenceRows = (subjectName: string, preferences: TeacherPreference[]) =>
    (allGrids || [])
      .filter(grid => (grid.classes || []).some((c: any) => c.className === subjectName))
      .map(grid => ({
        grid_id: grid.id,
        subject_id: subjectName,
        subject_name: subjectName,
        teacher_preferences: preferences,
        updated_at: new Date().toISOString(),
      }))

  // Save preferences to Supabase - now saves by subject name
  const savePreferences = async (subjectName: string, preferences: TeacherPreference[], classIds: string[]) => {
    if (!allGrids || allGrids.length === 0) return
    
    setIsSaving(true)
    try {
      // Add metadata to preferences
      const enhancedPreferences = preferences.map(pref => ({
        ...pref,
        addedBy: pref.addedBy || user?.email,
        addedAt: pref.addedAt || new Date()
      }))

      // Save to curriculum_preferences — one row per grid running this subject.
      const rows = buildPreferenceRows(subjectName, enhancedPreferences)
      if (rows.length === 0) {
        throw new Error(`No ${workingYear} grid contains "${subjectName}"`)
      }

      const { error } = await supabase
        .from('curriculum_preferences')
        .upsert(rows, { onConflict: CURRICULUM_PREFERENCES_CONFLICT_TARGET })

      if (error) throw error

      // Update local state
      setGroupedSubjects((prev) =>
        prev.map((subject) =>
          subject.subjectId === subjectName
            ? { 
                ...subject, 
                teacherPreferences: enhancedPreferences,
                lastModified: new Date(),
                modifiedBy: user?.email 
              }
            : subject
        )
      )
      
      addToast(`Saved ${preferences.length} nomination${preferences.length === 1 ? '' : 's'} for ${subjectName} (${workingYear})`, 'success')
      setShowPreferenceModal(false)
      setSelectedSubject(null)
    } catch (error) {
      console.error('Error saving assignments:', error)
      addToast('Failed to save assignments', 'error')
    } finally {
      setIsSaving(false)
    }
  }

  // Bulk assign preferences with Supabase batch operations
  const executeBulkAssignment = async () => {
    if (!allGrids || allGrids.length === 0) return
    
    const selectedItems = Array.from(selectedRows)
    const isSubjectMode = viewMode === 'subjects'
    
    if (bulkAssignState.targetSelection.length === 0) {
      addToast('Please select at least one item to assign', 'error')
      return
    }

    setIsSaving(true)
    try {
      const updatedSubjects = [...groupedSubjects]
      const batchUpdates = []
      let assignmentCount = 0

      if (isSubjectMode) {
        // Assigning teachers to subjects
        selectedItems.forEach(subjectId => {
          const subjectIndex = updatedSubjects.findIndex(s => s.subjectId === subjectId)
          if (subjectIndex >= 0) {
            bulkAssignState.targetSelection.forEach(teacherId => {
              const teacher = staff.find(s => s.id === teacherId)
              if (teacher) {
                // Remove existing preference if any
                updatedSubjects[subjectIndex].teacherPreferences = 
                  updatedSubjects[subjectIndex].teacherPreferences.filter(p => p.teacherId !== teacherId)
                
                // Add new preference
                updatedSubjects[subjectIndex].teacherPreferences.push({
                  teacherId,
                  teacherCode: getStaffCode(teacher) || '',
                  teacherName: teacher.name || teacher.fullName,
                  preferenceLevel: bulkAssignState.preferenceLevel,
                  addedBy: user?.email,
                  addedAt: new Date()
                })
                assignmentCount++
              }
            })

            // Prepare batch update — same (grid, subject) grain as savePreferences.
            batchUpdates.push(
              ...buildPreferenceRows(
                updatedSubjects[subjectIndex].subjectName,
                updatedSubjects[subjectIndex].teacherPreferences
              )
            )
          }
        })
      }

      // Execute batch update
      if (batchUpdates.length > 0) {
        const { error } = await supabase
          .from('curriculum_preferences')
          .upsert(batchUpdates, { onConflict: CURRICULUM_PREFERENCES_CONFLICT_TARGET })

        if (error) throw error
      }
      
      setGroupedSubjects(updatedSubjects)
      
      const message = isSubjectMode
        ? `Assigned ${bulkAssignState.targetSelection.length} teachers to ${selectedItems.length} subjects`
        : `Assigned ${bulkAssignState.targetSelection.length} subjects to ${selectedItems.length} teachers`
      
      addToast(message, 'success')
      setSelectedRows(new Set())
      setShowBulkModal(false)
      setBulkAssignState({
        mode: 'subjects',
        selectedItems: [],
        searchTerm: '',
        filteredItems: [],
        targetSelection: [],
        preferenceLevel: 'perfect_fit'
      })
    } catch (error) {
      console.error('Error bulk assigning:', error)
      addToast('Failed to complete bulk assignment', 'error')
    } finally {
      setIsSaving(false)
    }
  }

  // Export preferences to CSV
  const exportPreferences = () => {
    const headers = ['Subject', 'Learning Area', 'Level', 'Class Count', 'Grid Names', 'Teacher', 'Teacher Code', 'Preference Level', 'Notes', 'Added By', 'Added Date']
    const rows = groupedSubjects.flatMap((subject) =>
      subject.teacherPreferences.length > 0
        ? subject.teacherPreferences.map((pref) => [
            subject.subjectName,
            subject.curriculumArea,
            subject.level || '',
            subject.classCount.toString(),
            subject.gridNames.join(', '),
            pref.teacherName,
            pref.teacherCode,
            pref.preferenceLevel,
            pref.notes || '',
            pref.addedBy || '',
            pref.addedAt ? new Date(pref.addedAt).toLocaleDateString() : ''
          ])
        : [[subject.subjectName, subject.curriculumArea, subject.level || '', subject.classCount.toString(), subject.gridNames.join(', '), 'No assignments set', '', '', '', '', '']]
    )

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `curriculum_assignments_${userCurriculumArea}_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    addToast('Assignments exported successfully', 'success')
  }

  // Get all departments
  const departments = useMemo(() => {
    const deptSet = new Set<string>()
    staff.forEach((s) => {
      if (s.department) {
        deptSet.add(s.department)
      }
    })
    return ['all', ...Array.from(deptSet).sort()]
  }, [staff])

  // Get all curriculum areas
  const curriculumAreas = useMemo(() => {
    const areaSet = new Set<string>()
    groupedSubjects.forEach((s) => {
      if (s.curriculumArea) {
        areaSet.add(s.curriculumArea)
      }
    })
    return ['all', ...Array.from(areaSet).sort()]
  }, [groupedSubjects])

  // Get preference statistics
  const getPreferenceStats = () => {
    const totalSubjects = groupedSubjects.length
    const totalClasses = groupedSubjects.reduce((sum, s) => sum + s.classCount, 0)
    const subjectsWithPreferences = groupedSubjects.filter(s => s.teacherPreferences.length > 0).length
    const totalPreferences = groupedSubjects.reduce((sum, s) => sum + s.teacherPreferences.length, 0)
    const perfectFitPreferences = groupedSubjects.reduce(
      (sum, s) => sum + s.teacherPreferences.filter(p => p.preferenceLevel === 'perfect_fit').length,
      0
    )
    const conflicts = groupedSubjects.filter(s => 
      s.teacherPreferences.filter(p => p.preferenceLevel === 'perfect_fit').length > s.classCount
    ).length
    
    return {
      totalSubjects,
      totalClasses,
      subjectsWithPreferences,
      coveragePercent: totalSubjects > 0 ? Math.round((subjectsWithPreferences / totalSubjects) * 100) : 0,
      totalPreferences,
      perfectFitPreferences,
      conflicts,
      averagePreferencesPerSubject: subjectsWithPreferences > 0 
        ? (totalPreferences / subjectsWithPreferences).toFixed(1)
        : '0',
    }
  }

  const stats = getPreferenceStats()

  // Toggle row expansion
  const toggleRowExpansion = (id: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  // Toggle row selection
  const toggleRowSelection = (id: string) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  // Select all visible
  const selectAllVisible = () => {
    if (viewMode === 'subjects') {
      const allIds = new Set(filteredGroupedSubjects.map(s => s.subjectId))
      setSelectedRows(allIds)
    } else {
      const allIds = new Set(filteredTeachers.map(t => t.teacherId))
      setSelectedRows(allIds)
    }
  }

  // Clear selections
  const clearSelections = () => {
    setSelectedRows(new Set())
  }

  // Render bulk assignment modal
  const renderBulkAssignModal = () => {
    const availableTeachers = staff.filter(s => {
      const isActive = s.status === 'active' || s.active === true || s.isActive === true
      if (!isActive) return false
      if (bulkAssignState.searchTerm) {
        const searchTerm = bulkAssignState.searchTerm.toLowerCase()
        return (s.name || s.fullName || '').toLowerCase().includes(searchTerm) ||
               (s.initial || s.staffCode || '').toLowerCase().includes(searchTerm) ||
               (s.department || '').toLowerCase().includes(searchTerm)
      }
      return true
    })

    const selectedSubjectsForDisplay = groupedSubjects.filter(s => 
      bulkAssignState.selectedItems.includes(s.subjectId)
    )

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-card-title text-ink-900">
                Bulk Assign Teachers
              </h3>
              <p className="text-sm text-ink-500 mt-1">
                Assign teachers to {bulkAssignState.selectedItems.length} selected subjects
              </p>
            </div>
            <button
              className="text-ink-400 hover:text-ink-500 text-2xl leading-none"
              onClick={() => setShowBulkModal(false)}
            >
              ×
            </button>
          </div>

          {/* Selected Subjects Summary */}
          <div className="mb-6 bg-navy-50 border border-navy-200 p-4 rounded-md">
            <h4 className="text-sm font-medium text-navy-800 mb-2">Selected Subjects ({selectedSubjectsForDisplay.length})</h4>
            <div className="max-h-32 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {selectedSubjectsForDisplay.map(subject => (
                  <div key={subject.subjectId} className="font-mono tabular-nums text-sm text-navy-700 bg-navy-100 px-2 py-1 rounded">
                    {subject.subjectName}
                    {subject.classCount > 1 && (
                      <span className="ml-1 text-xs text-navy-600">({subject.classCount}x)</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Teacher Search and Selection */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-medium text-ink-700">
                Select Teachers to Assign
              </h4>
              <div className="flex items-center space-x-3">
                <select
                  className="border border-ink-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-navy-500 focus:border-navy-500 text-sm"
                  value={bulkAssignState.preferenceLevel}
                  onChange={(e) => setBulkAssignState(prev => ({ 
                    ...prev, 
                    preferenceLevel: e.target.value as any 
                  }))}
                >
                  <option value="perfect_fit">Perfect Fit</option>
                  <option value="great_fit">Great Fit</option>
                  <option value="good_fit">Good Fit</option>
                  <option value="avoid">Avoid</option>
                </select>
              </div>
            </div>

            {/* Teacher Search */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  className="block w-full border border-ink-300 rounded-md shadow-sm py-2 pl-10 pr-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
                  placeholder="Search teachers by name, code, or learning area..."
                  value={bulkAssignState.searchTerm}
                  onChange={(e) => setBulkAssignState(prev => ({ ...prev, searchTerm: e.target.value }))}
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-ink-400" />
              </div>
            </div>

            {/* Available Teachers */}
            <div className="border rounded-lg max-h-64 overflow-y-auto bg-ink-50">
              {availableTeachers.length === 0 ? (
                <div className="p-8 text-center text-ink-500">
                  <Users className="h-8 w-8 mx-auto mb-2" />
                  <p>No teachers found</p>
                </div>
              ) : (
                <div className="p-4 space-y-2">
                  {availableTeachers.map(teacher => (
                    <label
                      key={teacher.id}
                      className={`flex items-center p-2 rounded cursor-pointer transition-colors ${
                        bulkAssignState.targetSelection.includes(teacher.id)
                          ? 'bg-navy-100 border border-navy-300'
                          : 'bg-white border border-ink-200 hover:bg-ink-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mr-3 rounded border-ink-300 text-navy-600 focus:ring-navy-500"
                        checked={bulkAssignState.targetSelection.includes(teacher.id)}
                        onChange={(e) => {
                          setBulkAssignState(prev => ({
                            ...prev,
                            targetSelection: e.target.checked
                              ? [...prev.targetSelection, teacher.id]
                              : prev.targetSelection.filter(id => id !== teacher.id)
                          }))
                        }}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-ink-900">
                          {teacher.name || teacher.fullName}
                        </div>
                        <div className="text-xs text-ink-500">
                          {teacher.initial || teacher.staffCode} • {teacher.department || 'No Learning Area'}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {bulkAssignState.targetSelection.length > 0 && (
              <div className="mt-3 text-sm text-navy-600">
                {bulkAssignState.targetSelection.length} teacher(s) selected
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-ink-500">
              This will assign {bulkAssignState.targetSelection.length} teacher(s) to {bulkAssignState.selectedItems.length} subject(s)
            </div>
            <div className="flex space-x-3">
              <Button variant="secondary" onClick={() => setShowBulkModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                leftIcon={isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
                onClick={executeBulkAssignment}
                disabled={isSaving || bulkAssignState.targetSelection.length === 0}
              >
                {isSaving ? 'Assigning...' : 'Assign Teachers'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Render preference modal with class count indicator
  const renderPreferenceModal = () => {
    if (!selectedSubject) return null

    const availableTeachers = staff.filter((s) => {
      const isActive = s.status === 'active' || s.active === true || s.isActive === true
      return isActive
    })

    // Check if we have enough preferences for the number of classes
    const needsMorePreferences = selectedSubject.teacherPreferences.filter(
      p => p.preferenceLevel === 'perfect_fit' || p.preferenceLevel === 'great_fit'
    ).length < selectedSubject.classCount

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-card-title text-ink-900">
                Teacher Assignments for {selectedSubject.subjectName}
              </h3>
              <p className="text-sm text-ink-500 mt-1">
                {displayLearningArea(selectedSubject.curriculumArea)} {selectedSubject.level && `• ${formatLevelName(selectedSubject.level)}`}
              </p>
              <div className="flex items-center mt-2 space-x-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-navy-100 text-navy-800">
                  <Hash className="h-4 w-4 mr-1" />
                  Runs {selectedSubject.classCount} {selectedSubject.classCount === 1 ? 'time' : 'times'}
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-navy-100 text-navy-800">
                  {selectedSubject.gridNames.join(', ')}
                </span>
                {needsMorePreferences && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-warn-bg text-warn-fg">
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    Need {selectedSubject.classCount - selectedSubject.teacherPreferences.filter(p => p.preferenceLevel === 'perfect_fit' || p.preferenceLevel === 'great_fit').length} more teacher(s)
                  </span>
                )}
              </div>
            </div>
            <button
              className="text-ink-400 hover:text-ink-500 text-2xl leading-none"
              onClick={() => setShowPreferenceModal(false)}
            >
              ×
            </button>
          </div>

          <div className="mb-6 bg-navy-50 border border-navy-200 p-4 rounded-md">
            <div className="flex">
              <Info className="h-5 w-5 text-navy-500 mr-2 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-navy-800 mb-1">Teacher Preference Guidelines</h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-navy-700">
                  <div>• <strong>Perfect Fit:</strong> Ideal teacher for this subject</div>
                  <div>• <strong>Great Fit:</strong> Excellent choice for assignment</div>
                  <div>• <strong>Good Fit:</strong> Suitable option worth considering</div>
                  <div>• <strong>Avoid:</strong> Should not be assigned this subject</div>
                </div>
                <div className="mt-2 pt-2 border-t border-navy-200">
                  <p className="text-sm text-navy-800">
                    <strong>Recommendation:</strong> Add at least {selectedSubject.classCount} teachers with Perfect Fit or Great Fit preference 
                    to ensure all {selectedSubject.classCount} {selectedSubject.classCount === 1 ? 'class' : 'classes'} can be staffed across {selectedSubject.gridNames.length} grid(s).
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6 border rounded-lg p-4 bg-ink-50">
            <h4 className="text-sm font-medium text-ink-700 mb-3">Add Teacher Assignment</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-ink-500 mb-1">
                  Teacher
                </label>
                <select
                  className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
                  id="teacher-select"
                >
                  <option value="">Select a teacher...</option>
                  {availableTeachers.map((teacher) => (
                    <option
                      key={teacher.id}
                      value={teacher.id}
                      disabled={selectedSubject.teacherPreferences.some(
                        (p) => p.teacherId === teacher.id
                      )}
                    >
                      {teacher.name || teacher.fullName} ({teacher.initial || teacher.staffCode || 'N/A'}) - {teacher.department || 'No Learning Area'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-500 mb-1">
                  Preference Level
                </label>
                <select
                  className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
                  id="preference-level"
                >
                  <option value="perfect_fit">Perfect Fit</option>
                  <option value="great_fit">Great Fit</option>
                  <option value="good_fit">Good Fit</option>
                  <option value="avoid">Avoid</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-500 mb-1">
                  &nbsp;
                </label>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    const teacherSelect = document.getElementById('teacher-select') as HTMLSelectElement
                    const preferenceLevel = document.getElementById('preference-level') as HTMLSelectElement
                    
                    if (!teacherSelect.value) {
                      addToast('Please select a teacher', 'error')
                      return
                    }

                    const teacher = availableTeachers.find((t) => t.id === teacherSelect.value)
                    if (!teacher) return

                    const teacherCode = getStaffCode(teacher)
                    if (!teacherCode) {
                      addToast(
                        `${teacher.name || teacher.fullName} has no staff code, so this nomination could not be matched to them. Add one in Staff Directory first.`,
                        'error'
                      )
                      return
                    }

                    const newPreference: TeacherPreference = {
                      teacherId: teacher.id,
                      teacherCode,
                      teacherName: teacher.name || teacher.fullName,
                      preferenceLevel: preferenceLevel.value as any,
                      addedBy: user?.email,
                      addedAt: new Date()
                    }

                    const updatedPreferences = [...selectedSubject.teacherPreferences, newPreference]
                    setSelectedSubject({
                      ...selectedSubject,
                      teacherPreferences: updatedPreferences,
                    })

                    teacherSelect.value = ''
                    addToast(`Added ${teacher.name || teacher.fullName}`, 'success')
                  }}
                >
                  Add Teacher
                </Button>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-medium text-ink-700">
                Current Assignments ({selectedSubject.teacherPreferences.length} / {selectedSubject.classCount} needed)
              </h4>
              {selectedSubject.teacherPreferences.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Trash2 className="h-4 w-4" />}
                  onClick={() => {
                    setSelectedSubject({
                      ...selectedSubject,
                      teacherPreferences: [],
                    })
                  }}
                >
                  Clear All
                </Button>
              )}
            </div>

            {selectedSubject.teacherPreferences.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-ink-300 rounded-lg bg-white">
                <Users className="h-12 w-12 text-ink-400 mx-auto mb-3" />
                <p className="text-ink-500">No teacher assignments set</p>
                <p className="text-sm text-ink-400 mt-1">
                  Add at least {selectedSubject.classCount} teacher(s) for the {selectedSubject.classCount} {selectedSubject.classCount === 1 ? 'class' : 'classes'}
                </p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden bg-white">
                <table className="min-w-full divide-y divide-ink-200">
                  <thead className="bg-ink-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Teacher
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Learning Area
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Preference
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Notes
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-ink-200">
                    {selectedSubject.teacherPreferences.map((pref, index) => {
                      const teacher = staff.find(s => s.id === pref.teacherId)
                      return (
                        <tr key={`${pref.teacherId}-${index}`} className="hover:bg-ink-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-ink-900">
                              {pref.teacherName}
                            </div>
                            <div className="font-mono tabular-nums text-sm text-ink-500">{pref.teacherCode}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-ink-500">
                              {teacher?.department || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                pref.preferenceLevel === 'perfect_fit'
                                  ? 'bg-ok-bg text-ok-fg'
                                  : pref.preferenceLevel === 'great_fit'
                                  ? 'bg-navy-100 text-navy-800'
                                  : pref.preferenceLevel === 'good_fit'
                                  ? 'bg-warn-bg text-warn-fg'
                                  : 'bg-danger-bg text-danger-fg'
                              }`}
                            >
                              {formatPreferenceLevel(pref.preferenceLevel)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <input
                              type="text"
                              className="w-full border border-ink-200 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-navy-500 focus:border-navy-500 text-sm"
                              placeholder="Optional notes..."
                              value={pref.notes || ''}
                              onChange={(e) => {
                                const updatedPreferences = [...selectedSubject.teacherPreferences]
                                updatedPreferences[index].notes = e.target.value
                                setSelectedSubject({
                                  ...selectedSubject,
                                  teacherPreferences: updatedPreferences,
                                })
                              }}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                            <button
                              className="text-danger-fg hover:text-danger-fg font-medium"
                              onClick={() => {
                                const updatedPreferences = selectedSubject.teacherPreferences.filter(
                                  (_, i) => i !== index
                                )
                                setSelectedSubject({
                                  ...selectedSubject,
                                  teacherPreferences: updatedPreferences,
                                })
                              }}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center">
            <div className="text-sm text-ink-500">
              {selectedSubject.lastModified && (
                <span>Last modified by {selectedSubject.modifiedBy}</span>
              )}
            </div>
            <div className="flex space-x-3">
              <Button variant="secondary" onClick={() => setShowPreferenceModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                leftIcon={isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                onClick={() =>
                  savePreferences(selectedSubject.subjectId, selectedSubject.teacherPreferences, selectedSubject.classIds)
                }
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Assignments'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Main render
  return (
    <div>
      {/* The year is in the title, not just the selector. A leader nominating
          teachers has to know which year they are nominating for before they
          start, and this page previously said nothing about it at all — the year
          was read silently from the shared year setting (issue #175). */}
      <PageHeader
        title={
          isCurriculumLeader
            ? `${userCurriculumArea} Teacher Assignments — ${workingYear}`
            : (isAssistantPrincipal || isHeadOfDepartment)
            ? `All Learning Areas — Teacher Assignments — ${workingYear}`
            : `Curriculum Teacher Assignments — ${workingYear}`
        }
        description={
          isCurriculumLeader
            ? `Nominating the teachers you want for ${userCurriculumArea} subjects in ${workingYear}.`
            : (isAssistantPrincipal || isHeadOfDepartment)
            ? `Nominating teachers across all learning areas for ${workingYear}.`
            : `Teacher nominations across all learning areas for ${workingYear}.`
        }
        actions={
          <div className="flex items-center space-x-2">
            <YearSelector showGridCount={false} />
            {lastSyncTime && (
              <span className="text-xs text-ink-500 mr-2">
                <Clock className="h-3 w-3 inline mr-1" />
                Synced {lastSyncTime.toLocaleTimeString()}
              </span>
            )}
            <Button
              variant="secondary"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={exportPreferences}
            >
              Export
            </Button>
          </div>
        }
      />

      {(() => {
        return null;
      })()}

      {isLoading ? (
        <Card>
          <div className="p-12 text-center">
            <Loader2 className="h-12 w-12 text-navy-500 mx-auto animate-spin" />
            <h3 className="mt-4 text-sm font-medium text-ink-900">Loading Assignments</h3>
            <p className="mt-1 text-sm text-ink-500">Processing multi-grid data...</p>
          </div>
        </Card>
      ) : (!allGrids || allGrids.length === 0) ? (
        <Card>
          <div className="p-12 text-center">
            <AlertTriangle className="h-12 w-12 text-warn-fg mx-auto" />
            <h3 className="mt-2 text-sm font-medium text-ink-900">No timetable grids available yet</h3>
            <p className="mt-1 text-sm text-ink-500">
              Teacher assignments appear here once a timetable grid has been set up.
            </p>
            <div className="mt-6">
              <Button variant="primary" onClick={() => navigate('/grid/multi-editor')}>
                Go to Timetable Editor
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <>
          {(() => {
            try {
              return null;
            } catch (error) {
              console.error('❌ Error in main content render:', error);
              return <div className="bg-danger-bg p-4 text-danger-fg">Error in render: {error.message}</div>;
            }
          })()}
          {/* Statistics Dashboard */}
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 mb-6">
            <Card className="lg:col-span-4">
              <div className="p-6">
                <h3 className="text-sm font-medium text-ink-900 mb-4">Assignment Overview</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="font-mono tabular-nums text-2xl font-bold text-ink-900">{stats.totalSubjects}</div>
                    <div className="text-sm text-ink-500">Unique Subjects</div>
                  </div>
                  <div>
                    <div className="font-mono tabular-nums text-2xl font-bold text-navy-600">{stats.totalClasses}</div>
                    <div className="text-sm text-ink-500">Total Classes</div>
                  </div>
                  <div>
                    <div className="font-mono tabular-nums text-2xl font-bold text-ok-fg">{stats.coveragePercent}%</div>
                    <div className="text-sm text-ink-500">Coverage</div>
                  </div>
                  <div>
                    <div className="font-mono tabular-nums text-2xl font-bold text-navy-600">{stats.totalPreferences}</div>
                    <div className="text-sm text-ink-500">Assignments</div>
                  </div>
                </div>
                
                <div className="mt-4 bg-ink-200 rounded-full h-3">
                  <div
                    className="bg-ok-solid h-3 rounded-full transition-all duration-500"
                    style={{ width: `${stats.coveragePercent}%` }}
                  />
                </div>
              </div>
            </Card>
            
            <Card className="lg:col-span-2">
              <div className="p-6">
                <h3 className="text-sm font-medium text-ink-900 mb-4">Quick Stats</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-ink-500">Perfect Fit</span>
                    <span className="font-mono tabular-nums text-sm font-medium">{stats.perfectFitPreferences}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-ink-500">Avg per Subject</span>
                    <span className="font-mono tabular-nums text-sm font-medium">{stats.averagePreferencesPerSubject}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-ink-500">Under-staffed</span>
                    <span className="font-mono tabular-nums text-sm font-medium text-warn-fg">{stats.conflicts}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Search
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      className="block w-full border border-ink-300 rounded-md shadow-sm py-2 pl-10 pr-10 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
                      placeholder="Search subjects..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Search className="absolute left-3 top-2.5 h-5 w-5 text-ink-400" />
                    {searchTerm && (
                      <button
                        className="absolute right-3 top-2.5 text-ink-400 hover:text-ink-500"
                        onClick={() => setSearchTerm('')}
                      >
                        <X className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Learning Area
                  </label>
                  <select
                    className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
                    value={curriculumAreaFilter}
                    onChange={(e) => setCurriculumAreaFilter(e.target.value)}
                    disabled={isCurriculumLeader} // Curriculum leaders are auto-filtered, assistant principals and heads of department can choose
                  >
                    {curriculumAreas.map(area => (
                      <option key={area} value={area}>
                        {area === 'all' ? 'All Learning Areas' : displayLearningArea(area)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Level
                  </label>
                  <select
                    className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
                    value={levelFilter}
                    onChange={(e) => setLevelFilter(e.target.value)}
                  >
                    <option value="all">All Levels</option>
                    <option value="oratory">Oratory</option>
                    <option value="junior">Junior (7-10)</option>
                    <option value="senior">Senior (11-12)</option>
                  </select>
                </div>
              </div>
              
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-ink-500">
                  Showing {filteredGroupedSubjects.length} {workingYear} subjects ({stats.totalClasses} total classes)
                  {selectedRows.size > 0 && (
                    <span className="ml-2 text-navy-600 font-medium">
                      • {selectedRows.size} selected
                    </span>
                  )}
                </div>
                
                {selectedRows.size > 0 && (
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setBulkAssignState({
                          mode: 'subjects',
                          selectedItems: Array.from(selectedRows),
                          searchTerm: '',
                          filteredItems: staff.filter(s => s.status === 'active' || s.active === true || s.isActive === true),
                          targetSelection: [],
                          preferenceLevel: 'perfect_fit'
                        })
                        setShowBulkModal(true)
                      }}
                    >
                      Bulk Assign Teachers
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearSelections}
                    >
                      Clear Selection
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* An empty list here means the year has no subjects for this leader —
              not that something broke. Reachable today: the 2027 Oratory and RUAH
              grids have no classes loaded yet (issue #175). */}
          {filteredGroupedSubjects.length === 0 ? (
            <Card>
              <div className="p-12 text-center">
                <Book className="h-12 w-12 text-ink-300 mx-auto" />
                <h3 className="mt-3 text-sm font-medium text-ink-900">
                  No {workingYear} subjects to nominate for
                </h3>
                <p className="mt-1 text-sm text-ink-500 max-w-md mx-auto">
                  {searchTerm || curriculumAreaFilter !== 'all' || levelFilter !== 'all'
                    ? 'No subjects match your current filters. Clear them to see everything available.'
                    : `The ${workingYear} timetable grids do not contain any subjects${
                        isCurriculumLeader ? ` for ${userCurriculumArea}` : ''
                      } yet. Once the grids for ${workingYear} are built, the subjects will appear here.`}
                </p>
              </div>
            </Card>
          ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-ink-200">
                <thead className="bg-ink-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          className="rounded border-ink-300 text-navy-600 focus:ring-navy-500"
                          checked={selectedRows.size === filteredGroupedSubjects.length && filteredGroupedSubjects.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              selectAllVisible()
                            } else {
                              clearSelections()
                            }
                          }}
                        />
                        <span>Subject</span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Learning Area
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Level
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Classes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Grids
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Assignments
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-ink-200">
                  {filteredGroupedSubjects.map((subject) => {
                    const needsMore = subject.teacherPreferences.filter(
                      p => p.preferenceLevel === 'perfect_fit' || p.preferenceLevel === 'great_fit'
                    ).length < subject.classCount
                    
                    return (
                      <React.Fragment key={subject.subjectId}>
                        <tr className={`hover:bg-ink-50 ${selectedRows.has(subject.subjectId) ? 'bg-navy-50' : ''}`}>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                className="mr-3 rounded border-ink-300 text-navy-600 focus:ring-navy-500"
                                checked={selectedRows.has(subject.subjectId)}
                                onChange={() => toggleRowSelection(subject.subjectId)}
                              />
                              <button
                                onClick={() => toggleRowExpansion(subject.subjectId)}
                                className="mr-2 text-ink-500 hover:text-ink-700"
                              >
                                {expandedRows.has(subject.subjectId) ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </button>
                              <div>
                                <div className="font-mono tabular-nums text-sm font-medium text-ink-900">
                                  {subject.subjectName}
                                </div>
                                {subject.lastModified && (
                                  <div className="text-xs text-ink-500">
                                    Modified by {subject.modifiedBy}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-navy-100 text-navy-800">
                              {displayLearningArea(subject.curriculumArea)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center text-sm text-ink-500">
                            {formatLevelName(subject.level)}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="font-mono tabular-nums inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-navy-100 text-navy-800">
                              <Hash className="h-3 w-3 mr-1" />
                              {subject.classCount}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <div className="text-sm text-ink-900">
                                {subject.gridNames.slice(0, 1).join(', ')}
                                {subject.gridNames.length > 1 && (
                                  <span className="text-ink-500"> +{subject.gridNames.length - 1}</span>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                leftIcon={<Eye className="h-3 w-3" />}
                                onClick={() => {
                                  // Find grid by name
                                  const grid = allGrids.find(g => subject.gridNames.includes(g.name))
                                  if (grid) {
                                    setSelectedGridForView(grid)
                                    setShowGridView(true)
                                  }
                                }}
                              >
                                View
                              </Button>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {subject.teacherPreferences.length === 0 ? (
                              <span className="text-ink-400 text-sm">None</span>
                            ) : (
                              <div className="flex items-center space-x-2">
                                <Users className="h-4 w-4 text-ink-400" />
                                <span className="font-mono tabular-nums text-sm text-ink-700">
                                  {subject.teacherPreferences.length}
                                </span>
                                {needsMore && (
                                  <AlertTriangle className="h-4 w-4 text-warn-fg" title="Needs more teachers" />
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                setSelectedSubject(subject)
                                setShowPreferenceModal(true)
                              }}
                            >
                              Assign
                            </Button>
                          </td>
                        </tr>
                        
                        {expandedRows.has(subject.subjectId) && subject.teacherPreferences.length > 0 && (
                          <tr>
                            <td colSpan={7} className="px-12 py-4 bg-ink-50">
                              <div className="space-y-2">
                                {subject.teacherPreferences.map((pref, i) => (
                                  <div key={i} className="flex items-center justify-between text-sm">
                                    <div className="flex items-center space-x-3">
                                      <span className="font-medium">{pref.teacherName}</span>
                                      <span className="font-mono tabular-nums text-ink-500">({pref.teacherCode})</span>
                                      <span
                                        className={`px-2 py-0.5 text-xs rounded-full ${
                                          pref.preferenceLevel === 'perfect_fit'
                                            ? 'bg-ok-bg text-ok-fg'
                                            : pref.preferenceLevel === 'great_fit'
                                            ? 'bg-navy-100 text-navy-800'
                                            : pref.preferenceLevel === 'good_fit'
                                            ? 'bg-warn-bg text-warn-fg'
                                            : 'bg-danger-bg text-danger-fg'
                                        }`}
                                      >
                                        {formatPreferenceLevel(pref.preferenceLevel)}
                                      </span>
                                    </div>
                                    {pref.notes && (
                                      <span className="text-ink-500 italic">{pref.notes}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>
          )}
        </>
      )}

      {/* Modals */}
      {showPreferenceModal && renderPreferenceModal()}
      {showBulkModal && renderBulkAssignModal()}

      {/* Read-only Grid View Modal */}
      {showGridView && selectedGridForView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h3 className="text-section text-ink-900">
                  Grid View: {selectedGridForView.name}
                </h3>
                <p className="text-sm text-ink-500 mt-1">
                  Read-only view • {selectedGridForView.classes?.length || 0} classes
                </p>
                <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-navy-100 text-navy-800">
                  <EyeOff className="h-3 w-3 mr-1" />
                  Curriculum Leaders cannot edit grids
                </div>
              </div>
              <button
                className="text-ink-400 hover:text-ink-500 text-2xl"
                onClick={() => {
                  setShowGridView(false)
                  setSelectedGridForView(null)
                }}
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <ReadOnlyGridViewer
                grid={selectedGridForView}
                curriculumAreaFilter={isCurriculumLeader ? userCurriculumArea : undefined} // Assistant principals and heads of department see all areas
              />
            </div>

            <div className="p-6 border-t">
              <div className="flex justify-between items-center">
                <div className="text-sm text-ink-500">
                  <strong>Note:</strong> This is a read-only view. Contact an administrator to make changes to the grid.
                </div>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowGridView(false)
                    setSelectedGridForView(null)
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CurriculumAssignments